import type { Model } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import { toAgentId, toModelId, useGetAgent, useGetModel } from '@/hooks/api';
import { findModelId, JSONType } from '@/utils';

import { useCreateExport } from './useCreateExport';

const getHeaders = function* (model: Model) {
  for (const [id, config] of Object.entries(model.fieldConfig)) {
    if (config.type === 'table' && config.fields) {
      for (const [subId, subConfig] of Object.entries(config.fields)) {
        yield subConfig.name ?? subId;
      }
    } else {
      yield config.name ?? id;
    }
  }
};

type CSVValue = string | undefined | null;

const getLineValues = (model: Model, variables: JSONType) => {
  const lines: Record<string, Record<string, CSVValue>[]> = {};
  for (const [id, config] of Object.entries(model.fieldConfig)) {
    if (config.type === 'table' && config.fields) {
      lines[id] = [];
      for (const row of variables?.[id] ?? []) {
        const line: Record<string, CSVValue> = {};

        for (const [subId, subConfig] of Object.entries(config.fields)) {
          line[subConfig.name ?? subId] = row?.[subId]?.value;
        }
        lines[id].push(line);
      }
    }
  }
  return lines;
};

const getMultiValues = (model: Model, prediction: JSONType) => {
  const multiValues: Record<string, CSVValue[]> = {};
  for (const [id, config] of Object.entries(model.fieldConfig)) {
    if (config.type === 'multi-value') {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      multiValues[config?.name ?? id] = (prediction?.[id] ?? []).map((candidate: any) => candidate.value);
    }
  }
  return multiValues;
};

const getKeyValues = (model: Model, variables: JSONType) => {
  const keyValues: Record<string, CSVValue> = {};
  for (const [id, config] of Object.entries(model.fieldConfig)) {
    if (config.type !== 'table' && !(config.type === 'multi-value')) {
      keyValues[config.name ?? id] = variables?.[id]?.value;
    }
  }
  return keyValues;
};

const getValues = (model: Model, variables: JSONType) => {
  return {
    keyValues: getKeyValues(model, variables),
    lines: getLineValues(model, variables),
    multiValues: getMultiValues(model, variables),
  };
};

const createCSVRows = (
  headers: string[],
  runId: string,
  keyValues: Record<string, CSVValue>,
  lineValues: Record<string, CSVValue>[]
) => {
  const escape = (s: string) => s.replace(/"/g, '\\"');
  const rows: string[] = [];
  for (const lineValue of lineValues) {
    const row: string[] = [];
    for (const header of headers) {
      let value = '';
      if (header === 'Run ID') {
        value = runId;
      } else if (header in lineValue) {
        value = lineValue[header] ?? '';
      } else if (header in keyValues) {
        value = keyValues[header] ?? '';
      }
      row.push(`"${escape(value)}"`);
    }
    rows.push(row.join(','));
  }
  return rows;
};

export type UseCreateCSVExportOpts = {
  agentId?: string | null;
  runIds?: string[] | null;
};

export const useCreateCSVExport = ({ agentId, runIds }: UseCreateCSVExportOpts) => {
  const { data: agent } = useGetAgent(toAgentId(agentId));
  const { data: model } = useGetModel(toModelId(findModelId(agent)));

  const { createExport } = useCreateExport({ agentId, runIds });

  const create = useCallback(async () => {
    if (!model) throw Error('Model does not exist');
    const headers = ['Run ID', ...getHeaders(model)];
    /* Add CSV headers */
    const rows = [headers.join(',')];

    for (const e of await createExport()) {
      const { keyValues, lines, multiValues } = getValues(model, e.result.variables);

      /* Add key-value fields */
      rows.push(...createCSVRows(headers, e.result.runId, keyValues, [{ '/headers': undefined }]));

      /* Add multi-value fields */
      for (const [fieldId, values] of Object.entries(multiValues)) {
        const lineValues = values.map((value) => ({ [fieldId]: value }));
        rows.push(...createCSVRows(headers, e.result.runId, keyValues, lineValues));
      }

      /* Add line items fields */
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [_, lineValues] of Object.entries(lines)) {
        rows.push(...createCSVRows(headers, e.result.runId, keyValues, lineValues));
      }
    }
    return new Blob([rows.join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
  }, [createExport, model]);

  return { create };
};
